import { getChatAPI } from '@/api/ChatApi';
import { IntermediateMessage } from '@/api/ApiResponse';
import { MessageId } from '@/models/Message';
import { mkToolCallsBtnTrackAttrs } from '@/analytics/props/msg';
import { ReactionButton } from '@/components/chat/FeedbackButtons';

import 'react-json-view-lite/dist/index.css';
import {
  Badge,
  Card,
  CardContent,
  Divider,
  IconButton,
  Modal,
  styled,
  Typography,
} from '@mui/material';
import { darkStyles, JsonView } from 'react-json-view-lite';
import { FC, Fragment, useCallback, useMemo, useState } from 'react';
import CloseIcon from '@mui/icons-material/Close';
import ToolIcon from '@mui/icons-material/Build';

type ToolsTooltipProps = {
  messageId: MessageId;
};

const ToolsTooltip: FC<ToolsTooltipProps> = ({
  messageId,
}: ToolsTooltipProps) => {
  const [tools, setTools] = useState<IntermediateMessage[]>([]);
  const [open, setOpen] = useState(false);
  const handleClose = () => setOpen(false);

  const handleOpen = async () => {
    try {
      const chatApi = getChatAPI();
      const { data } = await chatApi.getMessageMetadata({
        messageId: messageId,
      });
      if (data.tools.length) {
        setTools(data.tools);
        setOpen(true);
      }
    } catch (e) {
      console.error('Failed to fetch tool calls:', e);
    }
  };

  const calls = useMemo(() => {
    return (tools ?? []).filter((tool) => tool.kwargs.type === 'ai');
  }, [tools]);

  const numberOfCalls = useMemo(() => {
    return calls.reduce(
      (n: number, call) => n + (call.kwargs.tool_calls ?? []).length,
      0,
    );
  }, [calls]);

  const shouldExpandNode = useCallback(
    (level: number, _value: any, _field?: string) => {
      return level < 1;
    },
    [],
  );

  const callIdToOutputs = useMemo(() => {
    const outputs = (tools ?? []).filter((tool) => tool.kwargs.type === 'tool');
    const map: { [key: string]: string } = {};
    outputs.forEach((output) => {
      const toolCallId = output.kwargs.tool_call_id;
      if (toolCallId) {
        // In case the output is not a valid JSON, we will just display it as a string
        let data: any = output.kwargs.content ?? 'no output';
        try {
          data = JSON.parse(data);
        } catch (e) {
          console.error('Failed to parse tool output:', e, data);
        }
        map[toolCallId] = data;
      }
    });
    return map;
  }, [tools]);

  return (
    <>
      <ReactionButton
        onClick={handleOpen}
        {...mkToolCallsBtnTrackAttrs({ messageId })}
        size="small"
      >
        <StyledBadge badgeContent={numberOfCalls} max={9}>
          <ToolIcon fontSize="small" />
        </StyledBadge>
      </ReactionButton>
      <Modal
        open={open}
        onClose={handleClose}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <StyledCard>
          <CloseButton onClick={handleClose}>
            <CloseIcon fontSize="small" />
          </CloseButton>
          <CardContent>
            <Typography variant="h6" component="h6">
              Tools Used by Nora:
            </Typography>
            {calls.map((toolCall, index) => {
              return (
                <Fragment key={index}>
                  <ToolcallBlock>
                    {toolCall.kwargs.type === 'ai' &&
                      toolCall.kwargs.tool_calls?.map((oneCall) => {
                        return (
                          <>
                            <Typography
                              key={oneCall.id}
                              variant="subtitle2"
                              component="h6"
                              gutterBottom
                            >
                              {`${oneCall.name}( ${JSON.stringify(oneCall.args)}
                              )`}
                            </Typography>
                            {callIdToOutputs[oneCall.id] && (
                              <JsonCodeScrollbox>
                                <JsonView
                                  data={callIdToOutputs[oneCall.id]}
                                  shouldExpandNode={shouldExpandNode}
                                  style={darkStyles}
                                  clickToExpandNode
                                />
                              </JsonCodeScrollbox>
                            )}
                          </>
                        );
                      })}
                  </ToolcallBlock>
                  <Divider />
                </Fragment>
              );
            })}
          </CardContent>
        </StyledCard>
      </Modal>
    </>
  );
};

export default ToolsTooltip;

const ToolcallBlock = styled('div')`
  margin-bottom: 12px;
`;

const JsonCodeScrollbox = styled('div')`
  max-height: 200px;
  overflow: auto;
  padding: 4px;
  background: rgb(0, 43, 54);
  font-size: 0.75em;
  max-width: 95%;
`;

const StyledCard = styled(Card)`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 700px;
  height: 500px;
  overflow: auto;
`;

const StyledBadge = styled(Badge)`
  & .MuiBadge-badge {
    right: -8px;
    top: -4px;
    border: 0;
    padding: 0;
    font-size: 11px;
    font-weight: bold;
    color: ${({ theme }) => theme.palette.text.reversed};
    background: ${({ theme }) => theme.color['teal-100'].hex};
    width: 16px;
    height: 16px;
    min-width: 0;
  }
`;

const CloseButton = styled(IconButton)`
  position: absolute;
  top: ${({ theme }) => theme.spacing(1)};
  right: ${({ theme }) => theme.spacing(1)};
  padding: 0;
`;
